// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import ExamSliderCard from "./ExamSliderCard/ExamSliderCard";
import SelectExamButton from "./SelectExamButton/SelectExamButton";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Contexts
import { useExamSliderContext } from "src/contexts/exams/ExamSliderContext";

// Constants
import { examCardEnums } from "src/constants/enums";

// Types
import { SelectedExamCardType } from "src/services/exams/types";

// Includes all simulation and video cards
const NON_EXAM_CARD_TYPES: Array<string> = [
  examCardEnums.IOL_SIMULATION.algorithmPath,
  examCardEnums.PROGRESSIVE_SIMULATION.algorithmPath,
  examCardEnums.DRY_EYE_SIMULATION.algorithmPath,
  examCardEnums.VIDEO.algorithmPath,
];

const ExamSliderList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);
  const { selectedExamCards, defaultExamOrder } = useExamSliderContext();

  const formattedExamCards: Array<SelectedExamCardType & { label: string }> =
    useMemo(
      () =>
        selectedExamCards?.map((selectedExam) => {
          const enumValues = Object.values(examCardEnums);
          const label =
            enumValues.find(
              (enumValue) =>
                enumValue.algorithmPath === selectedExam?.examFieldType
            )?.label ?? "";

          return { ...selectedExam, label };
        }),
      [selectedExamCards]
    );

  const hasNoExams = useMemo(
    () =>
      !selectedExamCards?.some(
        (card) => !NON_EXAM_CARD_TYPES.includes(card.examFieldType)
      ),
    [selectedExamCards]
  );

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(3, 1fr)",
          },
          gap: "8px",
        }}
      >
        {defaultExamOrder.map((item) => (
          <SelectExamButton key={item.algorithmPath} item={item} />
        ))}
      </Box>

      <Grid item display="flex" flexDirection="column" gap="16px">
        {hasNoExams && (
          <Grid item height="82px" alignContent="center">
            <Typography color={onSurface} fontSize="16px" textAlign="center">
              {t("No Exams Selected.")}
            </Typography>
          </Grid>
        )}

        <Grid
          item
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(1, 1fr)", sm: "repeat(2, 1fr)" },
            gap: "8px",
          }}
        >
          {formattedExamCards.map((item, index) => {
            const hasMultiSameExams =
              formattedExamCards?.filter(
                (exam) => exam.examFieldType === item.examFieldType
              )?.length > 1;

            return (
              <ExamSliderCard
                id={item?.examFieldType}
                key={`${item?.examFieldType}-${item?.groupId}`}
                label={
                  hasMultiSameExams
                    ? `#${item?.examNumLabel ?? index + 1} - ${t(item?.label)}`
                    : t(item?.label)
                }
                groupId={item?.groupId}
                examAlgorithm={item?.examFieldType}
              />
            );
          })}
        </Grid>
      </Grid>
    </>
  );
};

export default ExamSliderList;
